import { modalService } from "@whatslly/ui-components/modals";
import styles from "./Modals.module.scss";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { getJSON, postJSON } from "@whatslly/commons/apiUtils";
import { inject, injectable } from "inversify";
import { Button } from "@whatslly/ui-components/v2/Button";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { Field, FieldProps, FormikErrors, FormikProps } from "formik";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import type { PartialRecord } from "@whatslly/commons/typescriptUtils";
import { useTranslation } from "react-i18next";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import type { CRMServerToServerSettings, CRMCreateObjectSettings } from "@whatslly/types/messages-server/destinations";
import { GenerateCertificateResponse, GenerateCertificateResponseSuccess, DESTINATION_INTEGRATION_TYPES } from "@whatslly/types/messages-server/destinations";
import omit from "lodash/omit";
import pick from "lodash/pick";
import { observer } from "mobx-react-lite";
import { FormCheckBox } from "@whatslly/ui-components/v2/FormElements/FormCheckBox";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";
import { parseOrReturnError } from "@whatslly/commons/parseOrReturnError";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { ServerToServerBodyResponse } from "@whatslly/types/AuthBodyRequest";
import { downloadBlob } from "@whatslly/ui-components/componentsUtils";
import { TFunction } from "i18next";
import { makeObservable, observable, action } from "mobx";
import { FormSelect } from "@whatslly/ui-components/v2/FormElements/FormSelect";
import type { IOrganizationStore } from "../../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../../services/IOrganizationStore";
import { FormPasswordTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput/FormPasswordTextInput";
import { KeyValueInput } from "../../SuperAdminOrgConfigs/KeyValueInput";
import { MoreInfoQuestionMark } from "../../../components/MoreInfoQuestionMark";
import type { DestinationFormProps, ExtractDestination } from "../utils/DestinationsListTypes";
import classNames from "classnames";

type SalesforceDestination = ExtractDestination<"salesforce">;
const AVAILABLE_FIELDS = ["name", "sfUserId", "phone"];

const SERVER_URLS = [
  { label: "login.salesforce.com", value: "https://login.salesforce.com" },
  { label: "test.salesforce.com", value: "https://test.salesforce.com" },
];

@injectable()
class ServerToServerStore {
  generateCertificatePromise: IPromiseBasedObservable<GenerateCertificateResponse | void> | undefined;
  generateServerToServerEtokenPromise: IPromiseBasedObservable<ServerToServerBodyResponse | void> | undefined;

  constructor(
    @inject(InjectUserStore) private readonly userStore: IUserStore,
    @inject(InjectOrganizationStore) private readonly orgStore: IOrganizationStore,
    @inject(FrontLogger) private readonly logger: FrontLogger
  ) {
    makeObservable(this, {
      generateCertificatePromise: observable,
      generateServerToServerEtokenPromise: observable,
      generateCertificate: action,
      generateSalesforceSererToServerEtoken: action,
    });
  }

  async generateCertificate(t: TFunction) {
    this.generateCertificatePromise = fromPromise(
      getJSON({
        urlSuffix: `/message/destinations/${await this.orgStore.orgId}/generate-certificate`,
        etoken: this.userStore.etoken ?? "",
      })
        .then((r) => parseOrReturnError<GenerateCertificateResponse>(r, this.logger))
        .then((r) => {
          if (r.success) {
            return r;
          }
          modalService.openModal(Toast, { text: t("destinations_page.generate_certificate_failed_toast"), type: "error" });
        })
        .catch(() => {
          modalService.openModal(Toast, { text: t("destinations_page.generate_certificate_failed_toast"), type: "error" });
        })
    );
  }
  generateSalesforceSererToServerEtoken(
    t: TFunction,
    {
      username,
      connectedAppConsumerKey,
      privateKey,
      serverUrl,
    }: {
      username?: string;
      connectedAppConsumerKey?: string;
      privateKey: string;
      serverUrl: "https://login.salesforce.com" | "https://test.salesforce.com";
    }
  ) {
    this.generateServerToServerEtokenPromise = fromPromise(
      postJSON({
        urlSuffix: `/sfdc/v2/auth/server-to-server`,
        body: JSON.stringify({
          username,
          privateKey,
          connectedAppConsumerKey,
          serverUrl,
        }),
      })
        .then((r) => parseOrReturnError<ServerToServerBodyResponse>(r, this.logger))
        .then((r) => {
          if (r.success) {
            return r;
          }
          modalService.openModal(Toast, { text: t("destinations_page.test_connection_failed_toast"), type: "error" });
        })
        .catch(() => {
          modalService.openModal(Toast, { text: t("destinations_page.test_connection_failed_toast"), type: "error" });
        })
    );
  }
  downloadCertificate(certificate: string | undefined) {
    if (certificate) {
      downloadBlob(atob(certificate), "tuvis-cert.pem", "application/x-pem-file");
      return;
    }
    if (this.generateCertificatePromise?.state === "fulfilled" && this.generateCertificatePromise.value?.success === true) {
      downloadBlob(atob(this.generateCertificatePromise.value.certificate), "tuvis-cert.pem", "application/x-pem-file");
    }
  }
}

type ServerToServerFormDataTypeFields = "username" | "connectedAppConsumerKey" | "storeMedia" | "serverUrl";
type ServerToServerFormDataType = Pick<CRMServerToServerSettings, ServerToServerFormDataTypeFields> & {
  createObjectSettingsEnabled: boolean;
  createObjectSettingsFields?: Record<string, string>;
  createObjectSettingsType?: string;
};

const CreateObjectSettings: React.FC<{
  formikContext: React.RefObject<FormikProps<ServerToServerFormDataType> | null>;
  createObjectSettings?: CRMCreateObjectSettings;
}> = ({ formikContext, createObjectSettings }) => {
  const { t } = useTranslation();

  const objectTypesOptions = useMemo(() => {
    return [
      { label: t("destinations_page.crm.lead"), value: "lead" },
      { label: t("destinations_page.crm.contact"), value: "contact" },
    ];
  }, [t]);

  const defaultSelectOption = useMemo(() => {
    return createObjectSettings?.type
      ? (objectTypesOptions.find((opt) => opt.value === createObjectSettings.type) ?? objectTypesOptions[0])
      : objectTypesOptions[0];
  }, [createObjectSettings, objectTypesOptions]);

  return (
    <>
      <div className={classNames(styles.field, styles.fieldCheckbox)}>
        <Label
          className={styles.filedLabel}
          text={
            <>
              <span className={styles.questionMark}>{t("destinations_page.crm.enableObjectSettingsCheckbox")}</span>
              <MoreInfoQuestionMark
                tooltipPlacement="top"
                tooltipText={`${t("destinations_page.available_fields")}:\n${AVAILABLE_FIELDS.join("\n")}`}
              />
            </>
          }
        />
        <FormCheckBox name={"createObjectSettingsEnabled"} paddingInline="none" />
      </div>
      <Field
        name={`createObjectSettings`}
        children={(fieldData: FieldProps<string, ServerToServerFormDataType>) => {
          if (!fieldData.form?.values["createObjectSettingsEnabled"]) {
            return null;
          }

          return (
            <>
              <div className={styles.field}>
                <Label className={styles.filedLabel} text={"Object Type"} required />
                <FormSelect<{ value: string; label: string }>
                  name={"createObjectSettingsType"}
                  paddingInline="none"
                  options={objectTypesOptions}
                  defaultValue={defaultSelectOption}
                  isClearable={false}
                  required
                />
              </div>
              <div className={styles.field}>
                <Label className={styles.filedLabel} text={"Object Fields"} required />
                <KeyValueInput
                  defaultValue={fieldData.form.values["createObjectSettingsFields"] ?? { "": "" }}
                  onChange={(newValue) => formikContext.current?.setFieldValue("createObjectSettingsFields", newValue)}
                  typeOfValue="string"
                  rowContainerClassName={styles.objectRowContainer}
                  keyPlaceholder={t("destinations_page.crm.field_name")}
                  valuePlaceholder={t("destinations_page.crm.field_value")}
                />
              </div>
            </>
          );
        }}
      />
    </>
  );
};

const ServerToServerTab: React.FC<
  DestinationFormProps<"salesforce"> & {
    state: ServerToServerStore;
  }
> = observer(({ onSubmitForm, settings, state, extraSettings }) => {
  const [formikErrorHasErrors, setFormikErrorHasErrors] = useState(true);
  const formikContext = useRef<FormikProps<ServerToServerFormDataType>>(null);
  const { t } = useTranslation();

  const validate = async (values: ServerToServerFormDataType) => {
    const errors: FormikErrors<PartialRecord<keyof ServerToServerFormDataType, string>> = {};
    if (!values.username) errors.username = t("field_is_required");
    if (!values.connectedAppConsumerKey) errors.connectedAppConsumerKey = t("field_is_required");
    if (
      values.createObjectSettingsEnabled &&
      (!values.createObjectSettingsType ||
        !values.createObjectSettingsFields ||
        Object.keys(values.createObjectSettingsFields).length === 0 ||
        Object.entries(values.createObjectSettingsFields).some(([key, value]) => !key || !value))
    )
      errors.createObjectSettingsEnabled = t("field_is_required");

    setFormikErrorHasErrors(Object.keys(errors).length > 0);
    return errors;
  };

  const isTestSubmitDisabled = useMemo(() => {
    const certFromServer = state.generateCertificatePromise?.state === "fulfilled" && state.generateCertificatePromise?.value?.success;
    const certFromSettings = settings?.type === "server-to-server" && settings.certificate;
    return (!certFromServer && !certFromSettings) || formikErrorHasErrors;
  }, [state.generateCertificatePromise?.state, formikErrorHasErrors]);

  const getFormValues = () => {
    const data = (formikContext.current?.values ?? {}) as ServerToServerFormDataType;
    return {
      ...data,
      serverUrl: typeof data.serverUrl === "object" && "value" in data.serverUrl ? (data.serverUrl as any).value : data.serverUrl,
    };
  };

  const getCertData = useCallback(() => {
    return state.generateCertificatePromise?.state === "fulfilled" && state.generateCertificatePromise.value?.success
      ? pick<GenerateCertificateResponseSuccess, "privateKey" | "certificate">(state.generateCertificatePromise.value ?? {}, [
          "privateKey",
          "certificate",
        ])
      : {
          certificate: settings?.type === "server-to-server" && settings.certificate ? settings.certificate : "",
          privateKey: settings?.type === "server-to-server" && settings.privateKey ? settings.privateKey : "",
        };
  }, [state.generateCertificatePromise?.state, settings]);

  const getCreateObjectSettings = useCallback(() => {
    const data = (formikContext.current?.values ?? {}) as ServerToServerFormDataType;
    if (!data.createObjectSettingsEnabled) {
      return {};
    }
    const settings: CRMCreateObjectSettings = {
      fields: data.createObjectSettingsFields ?? {},
      type:
        typeof data.createObjectSettingsType === "object" && "value" in data.createObjectSettingsType
          ? (data.createObjectSettingsType as any).value
          : data.createObjectSettingsType,
    };
    return {
      extraSettings: {
        createObjectSettings: settings,
      },
    };
  }, []);

  const getSubmitData = useCallback((): SalesforceDestination => {
    const certData = getCertData();
    return {
      type: "salesforce" as const,
      integrationType: DESTINATION_INTEGRATION_TYPES.MESSAGES,
      ...getCreateObjectSettings(),
      settings: {
        type: "server-to-server",
        etoken:
          state.generateServerToServerEtokenPromise?.state === "fulfilled" && state.generateServerToServerEtokenPromise.value?.success
            ? state.generateServerToServerEtokenPromise.value.etoken
            : "",
        ...omit(getFormValues(), ["createObjectSettingsEnabled", "createObjectSettingsFields", "createObjectSettingsType"]),
        ...certData,
      },
    };
  }, [getCertData, getCreateObjectSettings, state.generateServerToServerEtokenPromise?.state, state.generateServerToServerEtokenPromise?.value]);

  useEffect(() => {
    validate(getFormValues());
  }, []);

  const onSubmit = useCallback(async () => {
    state.generateSalesforceSererToServerEtoken(t, {
      ...getCertData(),
      ...omit(getFormValues(), ["createObjectSettingsEnabled", "createObjectSettingsFields", "createObjectSettingsType"]),
    });
    const res = await state.generateServerToServerEtokenPromise;
    if (res?.success) {
      onSubmitForm(getSubmitData());
    }
  }, []);

  return (
    <div>
      <div className={styles.serverToServer}>
        {(settings?.type === "server-to-server" && settings.certificate) ||
        (state.generateCertificatePromise?.state === "fulfilled" && state.generateCertificatePromise.value?.success === true) ? (
          <Button
            testId={"download-certificate-button"}
            onClick={() => state.downloadCertificate(settings?.type === "server-to-server" ? settings.certificate : undefined)}
            paddingInline={"small"}
          >
            {t("destinations_page.download_certificate")}
          </Button>
        ) : (
          <Button
            testId={"generate-certificate-button"}
            isSubmitting={state.generateCertificatePromise?.state === "pending"}
            disabled={state.generateCertificatePromise?.state === "pending"}
            onClick={() => state.generateCertificate(t)}
            paddingInline={"small"}
          >
            {t("destinations_page.generate_certificate")}
          </Button>
        )}
      </div>
      <FormElement
        initialValues={
          settings && settings.type === "server-to-server"
            ? {
                ...pick<CRMServerToServerSettings, ServerToServerFormDataTypeFields>(settings, [
                  "username",
                  "connectedAppConsumerKey",
                  "storeMedia",
                  "serverUrl",
                ]),
                createObjectSettingsFields: extraSettings?.createObjectSettings?.fields ?? { "": "" },
                createObjectSettingsType: extraSettings?.createObjectSettings?.type ?? "lead",
                createObjectSettingsEnabled: Object.keys(extraSettings?.createObjectSettings?.fields ?? {}).length > 0,
              }
            : {
                username: "",
                connectedAppConsumerKey: "",
                storeMedia: false,
                serverUrl: "https://login.salesforce.com",
                createObjectSettingsType: "lead",
                createObjectSettingsEnabled: false,
              }
        }
        validate={validate}
        onSubmit={onSubmit}
        formikRef={formikContext}
      >
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.server_url")} required />
          <FormSelect<{ value: string; label: string }>
            name={"serverUrl"}
            paddingInline="none"
            options={SERVER_URLS}
            defaultValue={
              settings?.type === "server-to-server" && settings.serverUrl
                ? (SERVER_URLS.find((opt) => opt.value === settings.serverUrl) ?? SERVER_URLS[0])
                : SERVER_URLS[0]
            }
            isClearable={false}
            required
          />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.connected_app_consumer_key")} required />
          <FormPasswordTextInput name={"connectedAppConsumerKey"} paddingInline="none" />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.username")} required />
          <FormTextInput name={"username"} paddingInline="none" />
        </div>
        <div className={classNames(styles.field, styles.fieldCheckbox)}>
          <Label className={styles.filedLabel} text={t("destinations_page.store_media")} />
          <FormCheckBox name={"storeMedia"} paddingInline="none" />
        </div>
        <CreateObjectSettings formikContext={formikContext} createObjectSettings={extraSettings?.createObjectSettings} />
      </FormElement>
      <ModalButtons
        testId={`sf-save-button`}
        submitText={
          state.generateServerToServerEtokenPromise?.state === "pending" ? t("destinations_page.test_connection") : t("destinations_page.save")
        }
        isSubmitDisabled={isTestSubmitDisabled || state.generateServerToServerEtokenPromise?.state === "pending"}
        onSubmit={() => formikContext.current?.submitForm()}
        hideCancel={true}
      />
    </div>
  );
});

export const SalesforceModalForm: React.FC<DestinationFormProps<"salesforce">> = observer(
  ({ onSubmitForm, settings, extraSettings, destinationType, batchJob }) => {
    const state = useLocalStoreWithInjectedDependencies(ServerToServerStore);

    return (
      <ServerToServerTab
        onSubmitForm={onSubmitForm}
        settings={settings}
        state={state}
        extraSettings={extraSettings}
        destinationType={destinationType}
        batchJob={batchJob}
      />
    );
  }
);
